<template>
    <div class="form-container">
      <!-- Form [start] -->
      <form @submit.prevent="handleSubmit" class="pb-4">
        <div class="row">
          <!-- Informations générales du site -->
          <div class="col-lg-8">
            <div class="card card-h-100">
              <div class="card-header pb-0">
                <h4 class="header-title">{{ $t('installationInfo.generalInfo') }}</h4>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="mb-2 col-12">
                    <label for="facility-name" class="form-label">{{ $t('installationInfo.siteName') }}</label>
                    <input type="text" class="form-control" id="facility-name" v-model="facility.name" required maxlength="30" :placeholder="$t('installationInfo.siteNamePlaceholder')" />
                  </div>
                  <div class="mb-2 col-12">
                    <label for="facility-client-name" class="form-label">{{ $t('installationInfo.clientName') }}</label>
                    <input type="text" class="form-control" id="facility-client-name" v-model.trim="facility.client_name" maxlength="30" :placeholder="$t('installationInfo.clientNamePlaceholder')" />
                  </div>
                  <div class="mb-2 col-12">
                    <label for="facility-admin" class="form-label">{{ $t('installationInfo.adminEmail') }}</label>
                    <div class="d-flex align-items-center">
                      <input type="email" class="form-control" id="facility-admin" v-model="facility.admin_email" :required="adminEmailRequired" maxlength="254" :placeholder="$t('installationInfo.adminEmailPlaceholder')" :readonly="facility.validated && !facility.is_leasing" />
                      <a v-if="!facility.validated && adminEmailRequired" href="#" class="text-nowrap ms-2" @click.prevent="resendAdminEmail">{{ $t('installationInfo.resendEmail') }}</a>
                    </div>
                  </div>
                  <div class="mb-2">
                    <label for="facility-address" class="form-label">{{ $t('installationInfo.address') }}</label>
                    <textarea class="form-control" id="facility-address" v-model="facility.address" required maxlength="250" :placeholder="$t('installationInfo.addressPlaceholder')" rows="2" @blur="updateAddress"></textarea>
                  </div>
                  <div class="mb-2">
                    <label for="facility-country" class="form-label">{{ $t('installationInfo.country') }}</label>
                    <input type="text" class="form-control" id="facility-country" v-model="facility.country" required maxlength="50" :placeholder="$t('installationInfo.countryPlaceholder')"/>
                  </div>
                  <div class="mb-2">
                      <div class="form-check form-switch">
                          <input class="form-check-input" type="checkbox" id="install-leasing" v-model="facility.is_leasing" disabled>
                          <label class="form-check-label" for="install-leasing">{{ $t('installationCreate.leasing') }}</label>
                      </div>
                  </div>
                  <div class="mb-2">
                      <div class="form-check form-switch">
                          <input class="form-check-input" type="checkbox" id="install-hexa-identifier" v-model="facility.hexa_identifier">
                          <label class="form-check-label" for="install-hexa-identifier">{{ $t('installationCreate.hexaIdentifier') }}</label>
                      </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Photo ou logo -->
          <div class="col-lg-4">
            <div class="card card-h-100">
              <div class="card-header pb-0">
                <h4 class="header-title">{{ $t('common.photoOrLogo') }}</h4>
              </div>
              <div class="card-body">
                <div class="d-flex h-100 flex-wrap justify-content-center align-items-center">
                  <!-- Affichage photo -->
                  <div v-if="facility.picture" class="media-viewer border rounded" id="facility-picture">
                      <img :src="facility.picture" alt="Photo du site" style="max-height:70%; width: auto" class="media-viewer-img object-fit-cover">
                      <div class="media-viewer-tools">
                          <button type="button" class="btn btn-white text-danger" @click="removePicture">
                              <i class="mdi mdi-delete me-1"></i>{{ $t('common.delete') }}
                          </button>
                      </div>
                  </div>
                  <!-- Ajouter photo -->
                  <div v-else class="media-add-file single-file">
                      <button type="button" class="btn btn-secondary" @click="handleFileUpload">{{ $t('common.addFile') }}</button>
                      <input type="file" id="addFileInput" class="d-none" ref="fileInput" @change="handleFileChange" />
                      <span class="help-block mt-1">
                          <small>{{ $t('common.imageRequirements') }}</small>
                      </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Géolocalisation -->
          <div class="col-12">
            <div class="card card-h-100">
              <div class="card-header pb-0">
                <h4 class="header-title">{{ $t('installationInfo.geolocation') }}</h4>
              </div>
              <div class="card-body">
                <GoogleMapLoader :address="facility.address" ref="mapRef" />
              </div>
            </div>
          </div>
        </div>

        <!-- Form actions -->
        <div class="form-actions-sticky d-flex justify-content-center align-items-center">
          <router-link to="/installation-list" class="btn btn-outline-secondary me-2">{{ $t('common.back') }}</router-link>
          <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
        </div>
      </form>
      <!-- Form [end] -->
    </div>
</template>

<script>
  import GoogleMapLoader from '@/components/googleMapLoader'
  import { mapSettings } from '@/constants/mapSettings'
  import { useUserStore } from '@/stores/userStore';
  import { useRoute } from 'vue-router';
  import { reactive, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { showToast } from '@/services/toast';

  export default {
  name: "InstallationEditInformation",
  components: {
      GoogleMapLoader
  },
  computed: {
    mapConfig () {
        return {
            ...mapSettings,
            center: { lat: 0, lng: 0 }
        }
    },
  },
  setup() {
      const userStore = useUserStore();
      const route = useRoute();
      const { t } = useI18n();
      const facilityId = parseInt(route.query.id);
      const mapRef = ref(null);

      const storeFacility = userStore.getFacility(facilityId);
      const facility = reactive(storeFacility.clone());
      // Figé au chargement : un email déjà renseigné ne peut pas être effacé
      const adminEmailRequired = !!facility.admin_email;

      const handleSubmit = async () => {
        try {
          await userStore.updateFacility(storeFacility, facility);
          showToast('success', t('common.updateSuccess'));
        } catch (e) {
          showToast('error', e);
        }
      };

      const updateAddress = () => {
          if (mapRef.value) {
              mapRef.value.loadMap(facility.value.address);
          }
      };

      const handleFileChange = (event) => {
          const file = event.target.files[0];
          if (file) {
              if (file.type.startsWith('image/')) {
                  const reader = new FileReader();
                  reader.onload = () => {
                      const base64Image = reader.result;
                      facility.picture = base64Image;
                  };
                  reader.readAsDataURL(file);
              } else {
                  showToast('error', t('common.invalidImage'));
              }
          }
      };

      const handleFileUpload = () => {
          document.getElementById('addFileInput').click();
      };

      const removePicture = () => {
          facility.picture = '';
      };

      const resendAdminEmail = async () => {
        try {
          await userStore.resendAdminEmail(facility.id, facility.admin_email);
          showToast('success', t('common.emailSent'));
        } catch (error) {
          showToast('error', error);
        }
      }

      return {
          facility,
          adminEmailRequired,
          handleSubmit,
          updateAddress,
          mapRef,
          handleFileChange,
          handleFileUpload,
          removePicture,
          resendAdminEmail,
      };
  },
}
</script>
